import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import { useCallback, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AlertMap from '../../features/AlertList/features/AlertCard/components/AlertMap/AlertMap';
import {
  selectSelectedAlertConfig,
  selectSortedPagedAlertList,
} from '../../features/AlertList/state/selectors/alertListSelectors';
import {
  setMapCenter,
  setMapPolygon,
  setSearchTerm,
  setSelectedAlert,
} from '../../features/AlertList/state/slices/alertListSlice';
import AlertWidgetRow from './components/AlertWidgetRow/AlertWidgetRow';
import { layoutConfig } from './config/alertListWidget.config';

const AlertListWidget = ({ route, dimensions, layout, canDeleteAlerts }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedAlertConfig = useSelector(selectSelectedAlertConfig);
  const sortedPagedAlertList = useSelector(selectSortedPagedAlertList);
  const config = layoutConfig[layout] ?? layoutConfig['1x1'];

  const handleSelectAlert = useCallback(
    (alert) => {
      dispatch(setSelectedAlert(alert));
      dispatch(setMapCenter(alert.processedCenter));
      dispatch(setMapPolygon(alert.processedPolygon));
    },
    [dispatch],
  );

  useEffect(() => {
    dispatch(setSearchTerm(''));
    if (sortedPagedAlertList?.length && config.hasMap) {
      handleSelectAlert(sortedPagedAlertList[0]);
    }
  }, [sortedPagedAlertList, config.hasMap, handleSelectAlert, dispatch]);

  return (
    <div className="h-100 d-flex flex-column">
      <WidgetTimeUpdate route={route} realtime />
      {config.hasMap && selectedAlertConfig.mapCenter && selectedAlertConfig.mapPolygon && (
        <div
          className="position-relative my-2"
          style={{ height: config.mapPreviewHeight, minHeight: config.mapPreviewHeight }}
        >
          <AlertMap alert={selectedAlertConfig.selectedAlert} />
        </div>
      )}
      <div
        className="d-flex align-items-center mt-2 mb-1 px-1"
        style={{
          fontSize: config.headerFontSize,
          lineHeight: 1.1,
        }}
      >
        <p className="mb-0 flex-grow-1 flex-shrink-1 flex-basis-3xs">Severity</p>
        <p className="mb-0 flex-grow-1 flex-shrink-1 flex-basis-3xs">Type</p>
        <p className="mb-0 flex-grow-1 flex-shrink-1 flex-basis-3xs">Sender</p>
      </div>
      <div className="w-100 overflow-auto">
        {!sortedPagedAlertList?.length && (
          <div className="w-100 text-center">No Results Found...</div>
        )}
        {sortedPagedAlertList.map((alert, index) => {
          const isSelected = selectedAlertConfig.selectedAlert?.message_id === alert.message_id;
          // canCloseAlert: true if alert has workplaceId or globalActionId
          const canCloseAlert =
            alert?.alertSource !== 'fema' &&
            !!(alert && (alert.workplaceId || alert.globalActionId));
          const alertRow = (
            <AlertWidgetRow
              variant={config.rowVariant}
              alert={alert}
              canCloseAlert={canCloseAlert}
              canDeleteAlerts={canDeleteAlerts}
            />
          );

          if (!config.selectableRows) {
            return (
              <div key={alert.message_id ?? index} className="my-1 overflow-hidden rounded-3">
                {alertRow}
              </div>
            );
          }

          return (
            <div
              key={alert.message_id ?? index}
              className={`my-1 rounded-3 overflow-hidden cursor-pointer ${isSelected ? 'bg-primary' : ''}`}
              onClick={() => handleSelectAlert(alert)}
            >
              {alertRow}
            </div>
          );
        })}
      </div>
      <div
        className={`mb-2 d-flex w-100 align-items-stretch mt-2 gap-2 flex-shrink-0 ${
          config.actionsDirection
        } ${dimensions[0] < 2 ? 'justify-content-center' : ''}
        `}
        style={{
          paddingBottom: layout === '1x2' ? 0 : undefined,
          fontSize: config.actionFontSize,
        }}
      >
        <Button
          className="m-0 px-3 py-2 rounded-3 w-100"
          style={{ fontSize: 'inherit', lineHeight: 1.2 }}
          onClick={() => navigate('/publicuser/alerts')}
          variant="primary"
        >
          All Alerts
        </Button>
        <Button
          className="m-0 px-3 py-2 rounded-3 w-100"
          style={{ fontSize: 'inherit', lineHeight: 1.2 }}
          onClick={() => navigate('/publicuser/createAlert')}
          variant="primary"
        >
          Create Alert
        </Button>
      </div>
    </div>
  );
};

export default AlertListWidget;
